// components/team/AddGalleryForm.jsx
// Version CORRIGÉE pour colonnes réelles de la table Galeries

import React, { useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { Building2, MapPin, Mail, Check } from "lucide-react";

const AddGalleryForm = ({ galleryRequest, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    nom: galleryRequest?.gallery_name || "",
    ville: galleryRequest?.gallery_city || "",
    pays: galleryRequest?.gallery_country || "",
    site_web: galleryRequest?.gallery_website || "",
    email_contact: "",
    telephone: "",
    adresse: "",
    code_postal: "",
    instagram: "",
    specialite: "Art contemporain",
    tags: [],
    artistes_représentés: []
  });

  const specialiteOptions = [
    "Art contemporain",
    "Art moderne", 
    "Photographie",
    "Street art",
    "Design",
    "Sculpture"
  ];

  const tagOptions = [
    "Émergents",
    "Établis",
    "International",
    "Local",
    "Abstrait",
    "Figuratif"
  ];

  const toggleTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.includes(tag)
        ? formData.tags.filter(t => t !== tag)
        : [...formData.tags, tag]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom.trim()) {
      alert("Le nom de la galerie est requis");
      return;
    }

    try {
      setLoading(true);

      // Insérer la galerie avec les VRAIS noms de colonnes
      const { data: newGallery, error } = await supabase
        .from("Galeries")
        .insert({
          nom: formData.nom,
          ville: formData.ville,
          pays: formData.pays,
          site_web: formData.site_web,
          email_contact: formData.email_contact,
          telephone: formData.telephone,
          adresse: formData.adresse,
          code_postal: formData.code_postal,
          instagram: formData.instagram,
          specialite: formData.specialite,
          tags: formData.tags.length > 0 ? `{${formData.tags.map(t => `"${t}"`).join(',')}}` : null,
          is_active: true,
          data_quality_score: 80,
          created_by: user?.id
        })
        .select()
        .single();

      if (error) throw error;

      // Marquer les demandes comme intégrées SEULEMENT si c'est lié à une demande
      if (galleryRequest?.request_ids) {
        await supabase
          .from("GalerieDemandes")
          .update({ 
            status: 'integrated',
            integrated_gallery_id: newGallery.id,
            integrated_at: new Date().toISOString()
          })
          .in("id", galleryRequest.request_ids);
      }

      alert(`✅ Galerie "${formData.nom}" ajoutée !`);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (e) {
      console.error("Erreur création galerie:", e);
      alert("Erreur lors de la création de la galerie: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-8">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-light text-gray-900">
                {galleryRequest?.request_ids ? "Ajouter la galerie" : "Nouvelle galerie"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {galleryRequest?.request_ids 
                  ? `Demandée par ${galleryRequest.request_ids.length} client(s)`
                  : "Ajout direct par la team"
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Infos de base */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Informations de base
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de la galerie *
              </label>
              <input
                type="text"
                required
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spécialité
              </label>
              <select
                value={formData.specialite}
                onChange={(e) => setFormData({ ...formData, specialite: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {specialiteOptions.map(spec => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Localisation */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Localisation
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ville
                </label>
                <input
                  type="text"
                  value={formData.ville}
                  onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pays
                </label>
                <input
                  type="text"
                  value={formData.pays}
                  onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="123 rue de la Galerie"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code postal
                </label>
                <input
                  type="text"
                  value={formData.code_postal}
                  onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="75001"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Contact
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site web
                </label>
                <input
                  type="url"
                  value={formData.site_web}
                  onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram
                </label>
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="@galeriename"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email_contact}
                  onChange={(e) => setFormData({ ...formData, email_contact: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="contact@galerie.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="+33 1 23 45 67 89"
                />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Tags
            </h3>

            <div className="flex flex-wrap gap-2">
              {tagOptions.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    formData.tags.includes(tag)
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {formData.tags.includes(tag) && (
                    <Check className="w-3 h-3 inline mr-1" />
                  )}
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? "Création..." : "Créer la galerie"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGalleryForm;